import {
	isAGGResponse,
	extractError,
} from '@atlassian/jira-issue-reliability-fetch/src/use-issue-experience-monitoring.tsx';
import type { OnSubmitCallbacks } from '../types.tsx';

/**
 * Type definition for GraphQL mutation responses.
 * This matches the structure returned by Relay mutations.
 */
type GraphQLMutationResponse = {
	jira?: {
		[key: string]: {
			errors?: Array<{ message?: string | null; [key: string]: unknown }> | null;
			success?: boolean;
		} | null;
	} | null;
};

/**
 * Type guard to check if a value matches the GraphQL mutation response structure.
 *
 * @param value - The value to check
 * @returns True if the value matches GraphQLMutationResponse structure
 */
const isGraphQLMutationResponse = (value: unknown): value is GraphQLMutationResponse => {
	return (
		typeof value === 'object' &&
		value !== null &&
		'jira' in value &&
		(typeof value.jira === 'object' || value.jira === null || value.jira === undefined)
	);
};

/**
 * Extracts the first field name from a GraphQL mutation response that contains errors or success status.
 * This allows us to dynamically determine which field was mutated without requiring the field name as a parameter.
 *
 * @param response - The GraphQL mutation response
 * @returns The field name if found, undefined otherwise
 */
export const extractFieldNameFromResponse = <T extends GraphQLMutationResponse>(
	response: T,
): string | undefined => {
	if (!response?.jira) {
		return undefined;
	}
	// Find the first key in jira that has either errors or success property
	for (const key in response.jira) {
		if (Object.prototype.hasOwnProperty.call(response.jira, key)) {
			const fieldResponse = response.jira[key];
			if (fieldResponse && ('errors' in fieldResponse || 'success' in fieldResponse)) {
				return key;
			}
		}
	}
	return undefined;
};

/**
 * Creates standardized onCompleted handler for Relay mutations that automatically
 * extracts and passes error messages to the onFail callback.
 *
 * This helper ensures consistent error handling across all field types by:
 * 1. Extracting error messages from GraphQL response when success is false
 * 2. Passing the error message to onFail so it displays properly instead of generic fallback
 *
 * @param callbacks - The success and failure callbacks
 * @returns An onCompleted handler ready to use in commit()
 */
export const createMutationErrorHandlers = ({
	onSuccess,
	onFail,
}: OnSubmitCallbacks): ((response: unknown) => void) => {
	return (response: unknown): void => {
		// Type guard: check if response matches GraphQL mutation structure
		if (!isGraphQLMutationResponse(response)) {
			onFail();
			return;
		}

		const fieldName = extractFieldNameFromResponse(response);
		// Check if response has the expected structure and success is true
		if (fieldName && response.jira?.[fieldName]?.success) {
			onSuccess();
			return;
		}
		// Extract full error object to preserve metadata like statusCode, extensions, etc.
		// Pass fieldName to avoid re-extracting it
		const fullError = isAGGResponse(response) ? extractError(response)?.[0] : undefined;
		const errorMessage = fullError?.message ?? undefined;

		if (errorMessage) {
			// Create error that preserves original error metadata
			const error = new Error(
				fieldName
					? `Relay error encountered while updating ${fieldName} - ${errorMessage}`
					: `Relay error encountered - ${errorMessage}`,
			);
			// Preserve all properties from the original error object
			if (fullError) {
				Object.assign(error, fullError);
			}
			onFail(error, errorMessage);
		} else {
			onFail();
		}
	};
};
